import { ArrowDown, ArrowUp, ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { cn } from "@/shared/lib/cn";

type SortableTableHeadProps = {
  label: string;
  columnKey: string;
  activeKey: string | null;
  direction: SortDirection;
  onSort: (key: string) => void;
  sortable?: boolean;
  className?: string;
  /** Pastille de tri moderne (par défaut) ; `false` garde les chevrons historiques. */
  plain?: boolean;
};

const thClass =
  "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted";
const plainThClass = "px-4 py-3.5 text-left text-[13px] font-semibold text-primary dark:text-foreground";

export function SortableTableHead({
  label,
  columnKey,
  activeKey,
  direction,
  onSort,
  sortable = true,
  className,
  plain = true,
}: SortableTableHeadProps) {
  const baseClass = plain ? plainThClass : thClass;

  if (!sortable) {
    return <th className={cn(baseClass, className)}>{label}</th>;
  }

  const isActive = activeKey === columnKey;

  if (plain) {
    // Pastille cliquable : flèche lime sur la colonne triée, double chevron discret sinon.
    return (
      <th className={cn(baseClass, className)} aria-sort={isActive ? (direction === "asc" ? "ascending" : "descending") : undefined}>
        <button
          type="button"
          onClick={() => onSort(columnKey)}
          className={cn(
            "group/sort -mx-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            isActive ? "bg-white/80 shadow-[0_1px_2px_rgb(12_38_33/0.08)] dark:bg-white/10" : "hover:bg-white/60 dark:hover:bg-white/5",
          )}
        >
          {label}
          {isActive ? (
            <span className="grid h-5 w-5 place-items-center rounded-md bg-accent text-accent-foreground" aria-hidden="true">
              {direction === "asc" ? <ArrowUp size={12} strokeWidth={2.5} /> : <ArrowDown size={12} strokeWidth={2.5} />}
            </span>
          ) : (
            <ChevronsUpDown
              size={14}
              className="text-muted opacity-60 transition-opacity duration-150 group-hover/sort:opacity-100"
              aria-hidden="true"
            />
          )}
        </button>
      </th>
    );
  }

  return (
    <th className={cn(baseClass, className)} aria-sort={isActive ? (direction === "asc" ? "ascending" : "descending") : undefined}>
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
      >
        {label}
        <span className="flex flex-col">
          <ChevronUp
            size={10}
            className={cn(
              "-mb-1",
              isActive && direction === "asc" ? "text-primary" : "text-muted/50",
            )}
          />
          <ChevronDown
            size={10}
            className={cn(isActive && direction === "desc" ? "text-primary" : "text-muted/50")}
          />
        </span>
      </button>
    </th>
  );
}
